import {
  CompanyId,
  GroupId,
  ProjectId,
  TeamId,
} from "@custom-types/base-types";
import { ProjectArchivingState } from "@custom-types/project-types";

/** List all the main routes that user can navigate */
export enum MainRoutes {
  /** The root page of the app which will be redirected to app route */
  root = "/",

  /** The main page of the app */
  app = "/:companyId",

  /** The projects page of the app */
  projects = "projects",

  /** The members page of the app */
  members = "users",

  /** The groups page of the app */
  groups = "groups",

  /** The analytics page of the app */
  analytics = "analytics",

  /** The workspace settings page of the app */
  settings = "settings",

  /** The feedback page of the app */
  feedback = "feedback",

  /** The forbidding page due to lack of permissions of the app */
  forbidden = "forbidden",

  /** Data Management page */
  dataManagement = "data-management",

  /** The teams page */
  teams = "teams",

  /** Account settings page */
  accountSettings = "account",

  /** Add credits page */
  credits = "tokens",

  /** Used to log out the user */
  logout = "/logout",

  /** Used to handle subscription expiration and let user to start a new trial */
  subscriptionExpired = "subscription-expired",
}

/** List all the sub-routes under credits */
export enum CreditRoutes {
  /** Route to handle redirection from stripe payment */
  purchaseSuccess = "purchase-success",
}

/** List all the tabs for a specific project */
export enum ProjectTabs {
  /** The overview tab of a project */
  overview = "details",

  /** The team tab of a project */
  members = "members",

  /** The data tab of a project */
  data = "data",

  /** The integrations tab of a project */
  integrations = "integrations",

  /** The Activity tab of a project */
  activity = "activity",

  /** The Settings tab of a project */
  settings = "settings",

  /** The annotations (markups included) tab of a project */
  annotations = "annotations",

  /** The Evaluations tab of a project */
  evaluation = "evaluation",

  /** The Snapshots tab of a project */
  snapshots = "snapshots",
}

/** List all the tabs for a specific group */
export enum GroupTabs {
  /** The Managers tab of a group */
  managers = "members",

  /**
   * The Projects tab of a group
   * @deprecated Use activeProjects and archivedProjects instead. This route is kept for backward compatibility
   * to support all possible use cases, such as existing bookmarks or redirects from the old Dashboard.
   */
  projects = "projects",

  /** The Active Projects tab of a group */
  activeProjects = "active-projects",

  /** The Archived Projects tab of a group */
  archivedProjects = "archived-projects",
}

/** List all the tabs for a specific member */
export enum MemberTabs {
  /** The overview tab of a member */
  overview = "profile",
}

export enum UsersTabs {
  /** The general tab of users page */
  members = "members",

  /** The teams tab of settings page */
  teams = "teams",
}

export enum SettingsTabs {
  /** The general tab of settings page */
  general = "general",

  /** The subscription tab of settings page */
  subscription = "subscription",

  /** The export tab of settings page */
  export = "export",
}

/** List of tabs in account settings */
export enum AccountSettingsTabs {
  /** The account and security tab of account settings */
  security = "security",

  /** The preferences tab of account settings */
  preferences = "preferences",

  /** The integrations account tab of account settings */
  integrations = "integrations",

  /** The privacy account tab of account settings */
  privacy = "privacy",
}

/** List all the tabs for analytics page */
export enum AnalyticsTabs {
  /** The workspace tab of analytics page */
  workspace = "workspace",

  /** The projects tab of analytics page */
  projects = "projects",

  /** The members tab of analytics page */
  members = "members",

  /** The groups tab of analytics page */
  groups = "groups",

  /** The in-house analytics tab (alpha) */
  inHouseAnalytics = "in-house-analytics",
}

/** List all the tabs for team page */
export enum TeamTabs {
  /** The overview tab of a team */
  overview = "overview",

  /** The members tab of a team */
  members = "members",
}

/** All the params to use with Router */
export type RouteParams = {
  /** The id of the company */
  companyId: CompanyId;

  /** The id of the project */
  projectId: ProjectId;

  /** The condition of the project. Used to have a type safety Tabs */
  projectStatus: ProjectArchivingState;

  /** The selected tab of a project */
  projectTabs: ProjectTabs;

  /** The ID of the group */
  groupId: GroupId;

  /** The selected tab of a group */
  groupTabs: GroupTabs;

  /** The ID of the member */
  memberId: string;

  /** The selected tab of a member */
  memberTabs: MemberTabs;

  /** The selected tab for analytics page */
  analyticsTabs: AnalyticsTabs;

  /** The selected tab for settings page */
  settingsTab: SettingsTabs;

  /** The selected tab for users page */
  usersTab: UsersTabs;

  /** The ID of a team */
  teamId: TeamId;

  /** The selected tab of a team */
  teamTabs: TeamTabs;

  /** The selected tab of account settings */
  accountSettingsTab: AccountSettingsTabs;
};

/** All the query search params that can be appended to the route */
export enum QueryParams {
  /** Company roles. Used to notify user about entering a forbidden page */
  workspaceRoles = "workspace_roles",

  /** Project permission. Used to notify user about entering a forbidden page */
  projectPermissions = "project_permissions",

  /** Company subscriptions. Used to notify user about entering a forbidden page */
  companySubscriptions = "company_subscriptions",

  /**
   * Original visited url. Used to redirect to the original page after user was redirected
   * to a forbidden page and it is no longer the case.
   */
  origin = "origin",

  /** User identity. Used to identify which user was notified about entering a forbidden page */
  user = "user",

  /** The language query */
  lang = "lang",

  /** The id of a project */
  projectId = "project_id",

  /** The selected time frame */
  timeFrame = "time_frame",

  /** Enable all the feature flags */
  enableBeta = "enableBeta",

  /** Enable all the feature alpha */
  enableAlpha = "enableAlpha",

  /** Enable dev features */
  enableDev = "enableDev",

  /**
   * Hide migrated workspaces from Sphere Legacy.
   * TODO: Once https://faro01.atlassian.net/browse/ST-1652 is implemented we can remove this.
   */
  hideMigratedWorkspaces = "hideMigratedWorkspaces",

  /** The current data type displayed in the project data tab */
  type = "type",

  /** Origin URL from where the account settings page were opened */
  settingsOriginUrl = "settingsOriginUrl",

  /** The current sort criteria */
  sortBy = "sortBy",

  /** Opens the Active Members dialog in Subscription Settings page. */
  activeMembers = "activeMembers",

  /** Allow sending new phrases to Localize from dev environments */
  allowTranslationOverride = "allowTranslationOverride",

  /** Enable dark mode theme for testing */
  enableDarkMode = "enableDarkMode",

  /** Enable sending errors to Sentry from numbered environments or localhost */
  enableSentry = "enableSentry",

  /** The ID of a snapshot to open in the side panel */
  snapshotId = "snapshotId",

  /** Latitude of the world map center, used as a deep link to restore the map view */
  lat = "lat",

  /** Longitude of the world map center, used as a deep link to restore the map view */
  lng = "lng",

  /** Zoom level of the world map, used as a deep link to restore the map view */
  zoom = "zoom",
}

/** List of all routes that were used by the deprecated HB Entry Page and still need to be supported */
export enum HBEntryPageRoutes {
  /** Route which was used in HB EntryPage to select a workspace */
  switchRoute = "/switch",

  /** Was used to allow the user to go to either COM or EU workspace page */
  serverSelectionRoute = "/server-selection",

  /** Was used to show a form to the user allowing them to request a trial */
  trialRequestRoute = "/trial-request",

  /** Was used to shows the user-module login prefilled with the sso-id */
  ssoRoute = "/sso/:ssoId",
}

/** The route for HBD account settings page */
export const HBDSettingRoute = `/${MainRoutes.app}/me`;
